import { NavLink, useParams } from 'react-router-dom'
import classes from './MonitoringSidebar.module.scss'
import { Text } from '@consta/uikit/Text'
import { IconTarget } from '@consta/icons/IconTarget'
import { IconGas } from '@consta/icons/IconGas'
import { IconTest } from '@consta/icons/IconTest'
import { IconDocFilled } from '@consta/icons/IconDocFilled'
import { useGetProjectUuidQuery } from '../../../../../services/projectService'
import { useState, useEffect } from 'react'

const MonitoringSidebar = () => {
    const { projectUuid } = useParams<{ projectUuid: string }>()
    
    // Получаем данные проекта
    const { data: projectData, isLoading } = useGetProjectUuidQuery(
        { uuid: projectUuid! },
        { skip: !projectUuid }
    );

    const [currentStage, setCurrentStage] = useState(1);
    const currentPath = window.location.pathname;

    // Обновляем currentStage при получении данных или изменении пути
    useEffect(() => {
        if (projectData?.current_stage) {
            setCurrentStage(projectData.current_stage);
        }
    }, [projectData?.current_stage]);

    // Также обновляем currentStage на основе текущего URL
    useEffect(() => {
        const pathMatch = currentPath.match(/\/monitoring\/(\d+)/);
        if (pathMatch) {
            const stageFromUrl = parseInt(pathMatch[1]);
            if (stageFromUrl > currentStage) {
                setCurrentStage(stageFromUrl);
            }
        }
    }, [currentPath, currentStage]);

    // Простая проверка доступности: stageNumber <= currentStage
    const isStageAvailable = (stageNumber: number) => {
        return stageNumber <= currentStage;
    };

    // Проверка активного этапа
    const isStageActive = (stageNumber: number) => {
        if (stageNumber === 1 && currentPath === `/platform/project/${projectUuid}/monitoring`) {
            return true;
        }
        if (stageNumber > 1 && currentPath === `/platform/project/${projectUuid}/monitoring/${stageNumber}`) {
            return true;
        }
        return false;
    };

    // Проверка завершенного этапа: stageNumber < currentStage И этап не активен
    const isStageCompleted = (stageNumber: number) => {
        return stageNumber < currentStage && !isStageActive(stageNumber);
    };

    // Получение пути для навигации
    const getStagePath = (stageNumber: number) => {
        if (stageNumber === 1) {
            return `/project/${projectUuid}/monitoring`;
        }
        return `/project/${projectUuid}/monitoring/${stageNumber}`;
    };

    // Рендеринг иконки этапа
    const renderStageIcon = (stageNumber: number) => {
        //@ts-ignore
        if (stageNumber === 1) return <IconTarget size='s' />;
        //@ts-ignore
        if (stageNumber === 2 || stageNumber === 3) return <IconGas size='s' />;
        //@ts-ignore
        if (stageNumber === 4) return <IconTest size='s' />;
        //@ts-ignore
        if (stageNumber === 5) return <IconDocFilled size='s' />;
        //@ts-ignore
        return <IconTarget size='s' />;
    };

    // Функция для определения класса этапа
    const getStageClass = (stageNumber: number) => {
        if (isStageActive(stageNumber)) {
            return `${classes.stageItem} ${classes.active}`;
        }
        if (isStageCompleted(stageNumber)) {
            return `${classes.stageItem} ${classes.completed}`;
        }
        if (isStageAvailable(stageNumber)) {
            return `${classes.stageItem} ${classes.available}`;
        }
        return `${classes.stageItem} ${classes.disabled}`;
    };

    // Рендеринг этапа
    const renderStageText = (stageNumber: number, title: string, subtitle: string) => {
        if (isStageAvailable(stageNumber)) {
            return (
                <NavLink 
                    to={getStagePath(stageNumber)} 
                    style={{ textDecoration: 'none' }}
                    className={classes.stageLink}
                >
                    <div className={getStageClass(stageNumber)}>
                        <div className={classes.stageContent}>
                            <div className={classes.stageIcon}>
                                {renderStageIcon(stageNumber)}
                            </div>
                            <div className={classes.stageText}>
                                <Text view='secondary' size='s' className={classes.stageTitle}>
                                    {stageNumber}. {title}
                                </Text>
                                <Text view='secondary' size='xs' className={classes.stageSubtitle}>
                                    {subtitle}
                                </Text>
                            </div>
                        </div>
                    </div>
                </NavLink>
            );
        } else {
            return (
                <div className={getStageClass(stageNumber)} style={{ cursor: 'not-allowed' }}>
                    <div className={classes.stageContent}>
                        <div className={classes.stageIcon}>
                            {renderStageIcon(stageNumber)}
                        </div>
                        <div className={classes.stageText}>
                            <Text view='secondary' size='s' className={classes.stageTitle}>
                                {stageNumber}. {title}
                            </Text>
                            <Text view='secondary' size='xs' className={classes.stageSubtitle}>
                                {subtitle}
                            </Text>
                        </div>
                    </div>
                </div>
            );
        }
    };

    if (isLoading) {
        return (
            <div className={classes.container}>
                <Text weight="semibold" className={classes.title}>Этапы мониторинга</Text>
                <Text>Загрузка...</Text>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Text weight="semibold" className={classes.title}>Этапы мониторинга</Text>
            
            <div className={classes.stagesList}>
                {/* Этап 1: Информация о проекте */}
                {renderStageText(1, 'Информация о проекте', 'Основные сведения')}
                
                {/* Этап 2: Скважины */}
                {renderStageText(2, 'Скважины', 'Мини-отчёт 1')}
                
                {/* Этап 3: Программа отбора проб */}
                {renderStageText(3, 'Программа отбора проб', 'Этикетки для тары')}
                
                {/* Этап 4: Сверка и измерения воды */}
                {renderStageText(4, 'Сверка и измерения воды', 'Мини-отчёт 2')}
                
                {/* Этап 5: Задание на исследование */}
                {renderStageText(5, 'Задание на исследование', 'Финальный отчет')}
            </div>
        </div>
    )
}

export default MonitoringSidebar